import React, { useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaGem, FaHandshake, FaUsers, FaStar } from "react-icons/fa";

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const teamMembers = [
    {
      id: 1,
      name: "Michael Thompson",
      role: "Master Jeweler & Founder",
      bio: "With over 15 years of experience, Michael founded Baltimore Grillz & Custom Jewelry with a vision to bring high-quality custom jewelry to the city. His expertise in fine jewelry craftsmanship and custom grillz has earned him a reputation for excellence.",
      image: "/images/team/team1.jpg",
    },
    {
      id: 2,
      name: "Jasmine Williams",
      role: "Design Specialist",
      bio: "Jasmine brings creativity and innovation to every piece she designs. With a background in fine arts and jewelry design, she works closely with clients to transform their ideas into stunning custom pieces that exceed expectations.",
      image: "/images/team/team2.jpg",
    },
    {
      id: 3,
      name: "Robert Johnson",
      role: "Diamond Expert",
      bio: "As our certified gemologist, Robert ensures that only the highest quality stones make it into our custom pieces. His expertise in diamond grading and sourcing guarantees our clients receive the best value and quality.",
      image: "/images/team/team3.jpg",
    },
    {
      id: 4,
      name: "Sophia Martinez",
      role: "Client Relations Manager",
      bio: "Sophia manages our client relationships with care and attention to detail. She ensures a smooth experience from consultation to delivery, making every client feel like part of the Baltimore Grillz family.",
      image: "/images/team/team4.jpg",
    },
  ];

  const values = [
    {
      id: 1,
      title: "Quality Craftsmanship",
      description:
        "We take pride in our meticulous attention to detail and use only premium materials in all our custom pieces.",
      icon: <FaGem />,
    },
    {
      id: 2,
      title: "Customer Satisfaction",
      description:
        "Your happiness is our priority. We work closely with you throughout the entire process to ensure your vision comes to life.",
      icon: <FaHandshake />,
    },
    {
      id: 3,
      title: "Excellence",
      description:
        "We continuously strive to improve our techniques and designs to deliver the highest quality products in the industry.",
      icon: <FaStar />,
    },
    {
      id: 4,
      title: "Community",
      description:
        "We are proud to be part of the Baltimore community and actively engage in initiatives that give back to our city.",
      icon: <FaUsers />,
    },
  ];

  return (
    <AboutContainer>
      <PageHeader>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          About Us
        </motion.h1>
        <motion.div
          className="underline"
          initial={{ width: 0 }}
          animate={{ width: "80px" }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          Crafting Excellence in Custom Jewelry Since 2010
        </motion.p>
      </PageHeader>

      <StorySection>
        <ContentWrapper>
          <TwoColumnGrid>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeIn}
            >
              <SectionTitle>Our Story</SectionTitle>
              <StoryText>
                <p>
                  Founded in 2010, Baltimore Grillz & Custom Jewelry began as a
                  small workshop with a big vision - to create custom jewelry
                  and grillz that blend traditional craftsmanship with
                  contemporary design.
                </p>
                <p>
                  What started as a passion project by our founder, Michael
                  Thompson, quickly grew into Baltimore's premier destination
                  for custom jewelry. Michael's dedication to quality and
                  personal touch resonated with clients who were looking for
                  unique pieces that told their personal stories.
                </p>
                <p>
                  Over the years, we've expanded our team with talented artisans
                  and specialists, each bringing their unique expertise to
                  create exceptional custom pieces. Today, we're proud to serve
                  clients not just from Baltimore, but across the country.
                </p>
                <p>
                  Throughout our journey, our core values have remained
                  unchanged - exceptional craftsmanship, personalized service,
                  and a commitment to bringing our clients' visions to life.
                  Every piece that leaves our workshop carries with it the
                  essence of these values and the heart of Baltimore.
                </p>
              </StoryText>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeIn}
            >
              <ImageContainer>
                <AboutImage
                  src="/images/about/workshop.jpg"
                  alt="Our workshop"
                />
              </ImageContainer>
            </motion.div>
          </TwoColumnGrid>
        </ContentWrapper>
      </StorySection>

      <MissionSection>
        <ContentWrapper>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center"
          >
            <SectionTitle>Our Mission & Vision</SectionTitle>
            <p className="mission-text">
              We are dedicated to crafting exceptional custom jewelry and grillz
              that celebrate individuality, artistry, and quality. Our vision is
              to be the most trusted name in custom jewelry, known for
              transforming personal stories into wearable works of art.
            </p>
          </motion.div>

          <ValueGrid
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {values.map((value) => (
              <ValueCard
                key={value.id}
                variants={fadeIn}
                whileHover={{
                  y: -10,
                  boxShadow: "0 15px 30px rgba(0, 0, 0, 0.3)",
                }}
              >
                <IconBox>{value.icon}</IconBox>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueCard>
            ))}
          </ValueGrid>
        </ContentWrapper>
      </MissionSection>

      <TeamSection>
        <ContentWrapper>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center"
          >
            <SectionTitle>Meet Our Team</SectionTitle>
            <p className="team-intro">
              Our dedicated team of experts brings together decades of
              experience in jewelry design, craftsmanship, and customer service.
            </p>
          </motion.div>

          <TeamGrid
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {teamMembers.map((member) => (
              <TeamCard
                key={member.id}
                variants={fadeIn}
                whileHover={{
                  y: -10,
                  boxShadow: "0 15px 30px rgba(0, 0, 0, 0.3)",
                }}
              >
                <TeamImageContainer>
                  <TeamImage placeholder={member.id} />
                </TeamImageContainer>
                <TeamInfo>
                  <TeamName>{member.name}</TeamName>
                  <TeamRole>{member.role}</TeamRole>
                  <TeamBio>{member.bio}</TeamBio>
                </TeamInfo>
              </TeamCard>
            ))}
          </TeamGrid>
        </ContentWrapper>
      </TeamSection>

      <AchievementsSection>
        <ContentWrapper>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <SectionTitle>Our Achievements</SectionTitle>
          </motion.div>

          <AchievementsGrid>
            <AchievementItem
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <AchievementNumber>10+</AchievementNumber>
              <AchievementText>Years of Excellence</AchievementText>
            </AchievementItem>

            <AchievementItem
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <AchievementNumber>5,000+</AchievementNumber>
              <AchievementText>Custom Pieces Created</AchievementText>
            </AchievementItem>

            <AchievementItem
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <AchievementNumber>98%</AchievementNumber>
              <AchievementText>Client Satisfaction</AchievementText>
            </AchievementItem>

            <AchievementItem
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <AchievementNumber>3</AchievementNumber>
              <AchievementText>Industry Awards</AchievementText>
            </AchievementItem>
          </AchievementsGrid>
        </ContentWrapper>
      </AchievementsSection>

      <CtaSection>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <SectionTitle>Ready to Create Your Custom Piece?</SectionTitle>
          <CtaText>
            Let's transform your ideas into stunning reality. Contact us today
            to begin your journey.
          </CtaText>
          <CtaButton
            whileHover={{
              scale: 1.05,
              boxShadow: "0 10px 25px rgba(255, 215, 0, 0.3)",
            }}
            whileTap={{ scale: 0.95 }}
            to="/contact"
          >
            Get Started
          </CtaButton>
        </motion.div>
      </CtaSection>
    </AboutContainer>
  );
};

// Styled Components
const AboutContainer = styled.div`
  background-color: var(--dark-bg);
  color: var(--white);
`;

const PageHeader = styled.div`
  padding: 150px 0 80px;
  text-align: center;
  background: linear-gradient(to bottom, var(--darker-bg), var(--dark-bg));
  position: relative;

  h1 {
    font-size: 4rem;
    text-transform: uppercase;
    font-weight: 800;
    background: var(--gold-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 20px;

    @media (max-width: 768px) {
      font-size: 3rem;
    }

    @media (max-width: 576px) {
      font-size: 2.5rem;
    }
  }

  .underline {
    height: 4px;
    background: var(--gold-gradient);
    margin: 0 auto;
    border-radius: 2px;
  }

  p {
    max-width: 800px;
    margin: var(--spacing-md) auto 0;
    font-size: 1.1rem;
    color: var(--silver);

    @media (max-width: 768px) {
      font-size: 1rem;
      padding: 0 var(--spacing-md);
    }
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);

  .text-center {
    text-align: center;
  }

  .mission-text,
  .team-intro {
    max-width: 800px;
    margin: 0 auto var(--spacing-lg);
    font-size: 1.2rem;
    color: var(--silver);
    line-height: 1.6;

    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: white;
  margin-bottom: var(--spacing-md);
  position: relative;
  display: inline-block;

  &:after {
    content: "";
    position: absolute;
    bottom: -15px;
    left: 0;
    width: 60px;
    height: 3px;
    background: var(--gold-gradient);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  ${ContentWrapper} .text-center & {
    &:after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const StorySection = styled.section`
  padding: var(--spacing-xl) 0;

  @media (max-width: 768px) {
    padding: var(--spacing-lg) 0;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  align-items: center;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }
`;

const StoryText = styled.div`
  p {
    margin-bottom: var(--spacing-md);
    font-size: 1.05rem;
    color: var(--silver);
    line-height: 1.8;

    &:last-child {
      margin-bottom: 0;
    }

    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const ImageContainer = styled.div`
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  height: 100%;
  min-height: 400px;
`;

const AboutImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;

  /* Placeholder styling - remove in production */
  background: linear-gradient(45deg, #1a1a1a, #2a2a2a);
`;

const MissionSection = styled.section`
  padding: var(--spacing-xl) 0;
  background: linear-gradient(
    to right,
    rgba(var(--gold-rgb), 0.05),
    rgba(var(--gold-rgb), 0.15)
  );

  @media (max-width: 768px) {
    padding: var(--spacing-lg) 0;
  }
`;

const ValueGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-lg);
  margin-top: var(--spacing-xl);

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border-radius: var(--radius-md);
  padding: var(--spacing-lg);
  border: 1px solid rgba(var(--gold-rgb), 0.1);
  transition: var(--transition-medium);

  @media (max-width: 576px) {
    padding: var(--spacing-md);
  }
`;

const IconBox = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(var(--gold-rgb), 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-md);

  svg {
    font-size: 1.8rem;
    color: var(--gold);
  }
`;

const ValueTitle = styled.h3`
  font-size: 1.4rem;
  color: white;
  margin-bottom: var(--spacing-sm);

  @media (max-width: 576px) {
    font-size: 1.2rem;
  }
`;

const ValueDescription = styled.p`
  font-size: 1rem;
  color: var(--silver);
  line-height: 1.6;
`;

const TeamSection = styled.section`
  padding: var(--spacing-xl) 0;

  @media (max-width: 768px) {
    padding: var(--spacing-lg) 0;
  }
`;

const TeamGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: var(--spacing-lg);

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const TeamCard = styled(motion.div)`
  display: flex;
  background: rgba(255, 255, 255, 0.02);
  border-radius: var(--radius-md);
  overflow: hidden;
  border: 1px solid rgba(var(--gold-rgb), 0.1);
  transition: var(--transition-medium);

  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const TeamImageContainer = styled.div`
  flex: 0 0 200px;

  @media (max-width: 576px) {
    flex: 0 0 250px;
  }
`;

const TeamImage = styled.div`
  width: 100%;
  height: 100%;
  background: ${(props) => {
    // This creates placeholder backgrounds with different gradients based on the member id
    // In production, replace with actual images
    const hue = (props.placeholder * 60) % 360;
    return `linear-gradient(45deg, hsl(${hue}, 50%, 30%), hsl(${hue + 40
      }, 60%, 40%))`;
  }};
  background-size: cover;
  background-position: center;
`;

const TeamInfo = styled.div`
  padding: var(--spacing-md);
  flex: 1;
`;

const TeamName = styled.h3`
  font-size: 1.4rem;
  color: white;
  margin-bottom: 5px;

  @media (max-width: 576px) {
    font-size: 1.3rem;
  }
`;

const TeamRole = styled.p`
  font-size: 1rem;
  color: var(--gold);
  margin-bottom: var(--spacing-sm);
`;

const TeamBio = styled.p`
  font-size: 0.95rem;
  color: var(--silver);
  line-height: 1.6;
`;

const AchievementsSection = styled.section`
  padding: var(--spacing-xl) 0;
  background: var(--darker-bg);

  @media (max-width: 768px) {
    padding: var(--spacing-lg) 0;
  }
`;

const AchievementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const AchievementItem = styled(motion.div)`
  text-align: center;
  padding: var(--spacing-lg);
  background: rgba(255, 255, 255, 0.02);
  border-radius: var(--radius-md);
  border: 1px solid rgba(var(--gold-rgb), 0.1);

  @media (max-width: 576px) {
    padding: var(--spacing-md);
  }
`;

const AchievementNumber = styled.div`
  font-size: 3rem;
  font-weight: 800;
  background: var(--gold-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: var(--spacing-sm);

  @media (max-width: 576px) {
    font-size: 2.5rem;
  }
`;

const AchievementText = styled.p`
  font-size: 1.1rem;
  color: var(--silver);

  @media (max-width: 576px) {
    font-size: 1rem;
  }
`;

const CtaSection = styled.section`
  padding: var(--spacing-xl) 0;
  text-align: center;
  background: linear-gradient(
    45deg,
    rgba(var(--gold-rgb), 0.1),
    rgba(var(--gold-rgb), 0.2)
  );

  ${SectionTitle} {
    &:after {
      left: 50%;
      transform: translateX(-50%);
    }
  }

  @media (max-width: 768px) {
    padding: var(--spacing-lg) 0;
  }
`;

const CtaText = styled.p`
  font-size: 1.2rem;
  color: var(--silver);
  max-width: 700px;
  margin: 0 auto var(--spacing-lg);

  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0 var(--spacing-md);
  }
`;

const CtaButton = styled(motion.a)`
  display: inline-block;
  background: var(--gold-gradient);
  color: var(--black);
  border: none;
  border-radius: var(--radius-md);
  padding: 16px 30px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition-medium);
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 576px) {
    font-size: 1.1rem;
    padding: 14px 25px;
  }
`;

export default About;
